import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, ShieldAlert, Trash2 } from "lucide-react";
import BrowserHandoff from "@/components/earnos/BrowserHandoff";
import { apiFetch } from "@/lib/api";
import { Card, PageTitle, Spinner, useToast } from "@/components/earnos/kit";
const button = "rounded-xl border border-line bg-surface-3 px-4 py-2 text-sm font-semibold text-ink hover:border-brand disabled:opacity-50";
export default function BrowserAccess() {
  const navigate = useNavigate();
  const toast = useToast();
  const [domains, setDomains] = useState([]);
  const [runtime, setRuntime] = useState(null);
  const [handoffReady, setHandoffReady] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [authorizeCloudCost, setAuthorizeCloudCost] = useState(false);
  const [session, setSession] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [name, setName] = useState("");
  const [domainError, setDomainError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const [list, status, handoff] = await Promise.all([
        apiFetch("/browser/domains"), apiFetch("/browser/readiness"),
        apiFetch("/browser-host/readiness")
      ]);
      setDomains(Array.isArray(list) ? list : []);
      setRuntime(status);
      setHandoffReady(handoff);
      setError("");
    } catch (e) { setError(e.message || "Browser permissions unavailable."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  async function add(e) {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setDomainError(""); setBusy(true);
    try {
      await apiFetch("/browser/domains", { method: "POST", body: { domain: name.trim().toLowerCase() } });
      setName("");
      await refresh();
      toast.show("Website added to allowed list", "success");
    } catch (e) { setDomainError(e.message || "Could not allow this website"); toast.show(e.message, "error"); }
    finally { setBusy(false); }
  }
  async function remove(domain) {
    if (busy || !window.confirm("Revoke EARNOS browser access to " + domain + "?")) return;
    setBusy(true);
    try {
      await apiFetch("/browser/domains/" + encodeURIComponent(domain), { method: "DELETE" });
      await refresh();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  }
  async function startHandoff(e) {
    e.preventDefault();
    if (busy || !handoffReady?.handoff_configured || !authorizeCloudCost) return;
    setBusy(true);
    setError("");
    try {
      const result = await apiFetch("/browser-host/login-session", {
        method: "POST", body: {
          website_url: websiteUrl.trim(),
          authorize_cloud_browser_cost: authorizeCloudCost,
        },
      });
      setSession(result);
      setInspection(null);
      toast.show("Secure cloud browser session started", "success");
    } catch (e) { setError(e.message || "Could not start hosted browser"); }
    finally { setBusy(false); }
  }
  async function markFinished() {
    if (!session || busy) return;
    setBusy(true);
    try {
      const status = await apiFetch("/browser-host/login-session/" +
        encodeURIComponent(session.session_id) + "/finished", { method: "POST" });
      setSession(x => ({ ...x, customer_reported_login: status.customer_reported_login }));
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  }
  async function inspectSession() {
    if (!session || busy) return;
    setBusy(true);
    try {
      setInspection(await apiFetch("/browser-host/login-session/" +
        encodeURIComponent(session.session_id) + "/inspection"));
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  }
  async function stopSession() {
    if (!session || busy) return;
    setBusy(true);
    try {
      const result = await apiFetch("/browser-host/login-session/" +
        encodeURIComponent(session.session_id) + "/stop", { method: "POST" });
      setSession(null);
      setInspection(null);
      setAuthorizeCloudCost(false);
      toast.show(result.remote_release_confirmed ? "Cloud session closed" :
        "EARNOS access revoked; remote provider release not confirmed", "success");
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  }
  return (
    <div className="animate-fade-in space-y-5" data-testid="earnos-browser-access">
      <button onClick={() => navigate("/connections")} className="inline-flex items-center gap-2 text-ink-2 text-sm"><ArrowLeft size={17}/> Back to Connections</button>
      <div><PageTitle>Browser Access</PageTitle>
        <p className="text-sm text-ink-2 mt-2">Choose which public websites EARNOS may visit during customer-approved work.</p>
      </div>
      <Card>
        <p className="flex items-center gap-2 font-bold text-ink"><ShieldAlert size={18}/> Access rules</p>
        <p className="text-sm text-ink-2 mt-2">Adding a site does not sign you in, bypass a site's rules, or authorize posting, paying, or deleting. Each external action needs its own approval. A production cloud browser and secure sign-in handoff are not enabled until the server has a verified provider.</p>
      </Card>
      <Card>
        <h2 className="font-bold text-ink">Customer sign-in and website execution</h2>
        <p role="status" className="text-sm text-ink-2 mt-2">
          {runtime?.hosted_customer_login
            ? "Hosted sign-in is configured. Each action still requires an approved scope."
            : "Not live yet: connecting a domain alone cannot sign in or complete work on that website."}
        </p>
        {!runtime?.hosted_customer_login && runtime?.missing?.length ? (
          <details className="mt-3"><summary className="text-sm text-ink-2 cursor-pointer">What still needs to be enabled</summary><ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-ink-2">
            {runtime.missing.map(x => <li key={x}>{x}</li>)}
          </ul></details>
        ) : null}
      </Card>
      {handoffReady?.handoff_configured || session ? <Card>
        <h2 className="font-bold text-ink">Open a private browser</h2>
        <p className="text-sm text-ink-2 mt-2">If enabled by the EARNOS owner, open a short-lived hosted browser. You enter your own password and verification code in the browser's live view—never in EARNOS chat. Every agent action requires a separate preview and permission.</p>
        {!handoffReady?.handoff_configured ?
          <p className="text-sm text-amber-300 mt-3">Hosted browser provider has not been enabled on this EARNOS server.</p> :
          session ? <div className="mt-4 space-y-3">
            <p className="text-sm text-ink">Session expires at {new Date(session.expires_at).toLocaleString()}.</p>
            <a href={session.live_url} target="_blank" rel="noopener noreferrer"
              className={button + " inline-block"}>Open secure live browser</a>
            <p className="text-xs text-ink-2">Sign in yourself, then return here. This session cannot automatically make payments or approve work.</p>
            <div className="flex gap-2 flex-wrap">
              <button className={button} type="button" disabled={busy || session.customer_reported_login} onClick={markFinished}>
                {session.customer_reported_login ? "Sign-in reported" : "I've finished signing in"}
              </button>
              <button className={button} type="button" disabled={busy} onClick={inspectSession}>Inspect browser</button>
              <button className={button} type="button" disabled={busy} onClick={stopSession}>End session and revoke access</button>
            </div>
            {inspection ? <div className="rounded-xl bg-surface-3 p-3 space-y-1 text-sm text-ink-2">
              <p>Current page: {inspection.page_title || "Untitled"}</p>
              <p className="break-all">{inspection.url}</p>
              <p>Browser accessible. Account login is not independently verified.</p>
            </div> : null}
          </div> : <form onSubmit={startHandoff} className="mt-4 space-y-3">
            <label className="block text-sm font-bold text-ink" htmlFor="browser-website">Approved HTTPS website URL</label>
            <input id="browser-website" type="url" required maxLength={1500} value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://app.example.com"
              className="w-full rounded-xl bg-surface-3 border border-line p-3 text-ink" />
            <label className="flex gap-2 text-sm text-ink-2">
              <input type="checkbox" checked={authorizeCloudCost}
                onChange={e => setAuthorizeCloudCost(e.target.checked)} />
              I authorize creation of one short-lived cloud browser session. It may use EARNOS provider credits.
            </label>
            <button type="submit" className={button} disabled={busy || !authorizeCloudCost ||
                !websiteUrl.startsWith("https://")}>Open my private browser</button>
          </form>}
      </Card> : null}
      <Card>
        <form onSubmit={add} className="space-y-3">
          <label className="block text-sm font-bold text-ink" htmlFor="allowed-site">Website domain</label>
          <input id="allowed-site" type="text" value={name} onChange={e => { setName(e.target.value); setDomainError(""); }} aria-invalid={Boolean(domainError)} aria-describedby={domainError ? "domain-error" : undefined}
            placeholder="example.com" maxLength={253} className="w-full rounded-xl bg-surface-3 border border-line p-3 text-ink" />
          {domainError ? <p id="domain-error" role="alert" className="text-sm text-danger">{domainError}</p> : null}
          <button type="submit" disabled={busy || !name.trim()} className={button}>Allow this website</button>
        </form>
      </Card>
      <BrowserHandoff domains={domains} />
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
      <Card>
        <p className="font-bold text-ink mb-3">Allowed sites</p>
        {loading ? <Spinner /> : domains.length === 0 ? <p className="text-sm text-ink-2">No websites permitted yet.</p> :
          <div className="space-y-2">{domains.map(d => (
            <div key={d.domain} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface-3 p-3">
              <span className="flex items-center gap-2 text-ink text-sm break-all"><Globe size={16}/>{d.domain}</span>
              <button aria-label={"Revoke access to " + d.domain} disabled={busy} className={button} onClick={() => remove(d.domain)}><Trash2 size={15} className="inline mr-1"/>Revoke</button>
            </div>
          ))}</div>}
      </Card>
    </div>
  );
}

